import { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: { rooms: "Rooms", transfers: "Transfers", areas: "Areas", process: "Process", faq: "FAQ", cta: "Request a room" },
    hero: {
      eyebrow: "Delhi · Room booking assistance",
      lines: ["Land in Delhi.", "Rest in hours.", "Fly on, refreshed."],
      lede: "IGI Airport's premier transit sanctuary — verified rooms, zero friction. Sudheer Kumar personally checks availability and shares verified room photos with you before you pay a rupee.",
      ctaPrimary: "Request a room",
      ctaWhatsapp: "Book on WhatsApp",
      trust: "Every request is subject to hotel availability and final confirmation.",
      labelTitle: "Illustrative visual",
      labelSub: "Actual room photos are shared on chat.",
    },
    marquee: [
      "IGI Airport · T3 pickup", "Verified rooms only", "Photos on chat before you pay",
      "30% advance after approval", "Yashobhoomi Convention Centre", "Kapashera Border", "24×7 WhatsApp assistance",
    ],
    disclaimer: {
      lead: "How confirmation works —",
      text: "Whenever you have an inquiry, we will send actual room photos to you via chat. After you approve the photos, a 30% advance payment is required to confirm the booking.",
    },
    areas: {
      eyebrow: "Service areas",
      title: "Three corridors, one phone call",
      intro: "Every enquiry is handled personally. Pick your side of the city — we check verified options there and share actual photos on chat.",
      link: "View area guide",
      blurbs: {
        "igi-airport": "Rooms on the airport side of Delhi for layovers, red-eyes and early departures.",
        yashobhoomi: "Stay near India's largest convention centre in Dwarka Sector 25.",
        "kapashera-border": "Highway-side value stays between Delhi and Gurgaon.",
      },
    },
    rooms: {
      eyebrow: "01 · Room options",
      title: "Choose a starting point",
      intro: "Rates shown are the supplied room/package rates. Hotel name, exact address, taxes, availability and applicable conditions are confirmed before payment. Photos below are illustrative.",
      names: ["Couple stay", "Deluxe room · double occupancy", "Deluxe room · with breakfast", "Triple occupancy", "Triple occupancy · with breakfast"],
      notes: ["6 hours only — not a full-night stay", "Full stay", "Full stay · breakfast as stated", "Full stay", "Full stay · breakfast as stated"],
      tags: ["Short stay", "Most requested", "Breakfast", "Room", "Breakfast"],
      enquire: "Enquire for this option",
      onEnquiry: "on enquiry",
      aiTag: "AI visual · real photos on chat",
      noticeLead: ["Standard check-in:", "Standard check-out:"],
      notice: "12:00 PM|11:00 AM. Exact duration and hotel-specific terms must be confirmed.",
    },
    transfers: {
      eyebrow: "02 · Airport movement",
      title: "Pickup & drop, priced clearly",
      intro: "Pickup and drop are separate services. Pickup rates include additional parking charges as noted; confirm final fare and availability before booking.",
      pickup: "Pickup",
      drop: "Drop",
      ask: "Ask for availability",
      detail: "Pickup + parking charges",
      finePrint: "No tolls, waiting charges or other fees are added here. If applicable, any extra charges are confirmed before the ride is booked.",
    },
    process: {
      eyebrow: "03 · A calm, clear process",
      title: "Booking help without the guesswork",
      intro: "This is a booking assistance service, not an instant booking engine. You stay in control of the details before anything is confirmed — and you never pay before seeing the actual room.",
      steps: [
        ["01", "Send an enquiry", "Share your dates, guests, room preference and any transfer request."],
        ["02", "Availability check", "Sudheer Kumar checks the available hotel or transport option."],
        ["03", "Approve photos on chat", "Actual room photos are sent to you on WhatsApp. You approve them first."],
        ["04", "30% advance confirms it", "Pay a 30% advance only after approval. Balance and details are shared directly."],
      ],
    },
    enquiry: {
      eyebrow: "04 · Your enquiry",
      title: "Tell us what you need",
      intro: "Send your details on WhatsApp, or open a prepared email draft. Every enquiry also lands in Sudheer Kumar's secure inbox. No online payment is collected here.",
      role: "Booking assistance · Delhi",
      name: "Guest name",
      namePh: "Your full name",
      mobile: "Mobile / WhatsApp",
      mobilePh: "10-digit number",
      checkIn: "Check-in date",
      checkOut: "Check-out or 6-hour slot",
      checkOutPh: "Date or preferred slot",
      guests: "Number of guests",
      room: "Room / package",
      area: "Hotel / area preference",
      areaPh: "Select an area",
      breakfast: "Breakfast required",
      transfer: "Airport pickup or drop?",
      vehicle: "Vehicle",
      transferType: "Transfer type",
      special: "Special request",
      specialPh: "Flight timing, accessibility needs, or anything else",
      yes: "Yes",
      no: "No",
      pickupOpt: "Pickup",
      dropOpt: "Drop",
      consent: "I agree to be contacted about this enquiry and understand that the booking is subject to availability and confirmation.",
      submit: "Send on WhatsApp",
      sending: "Sending…",
      email: "Open email draft",
      success: "Saved to the inbox and opened on WhatsApp. Sudheer Kumar will confirm availability and share room photos on chat.",
    },
    trust: {
      eyebrow: "05 · Before you confirm",
      title: "Honest details make better stays",
      cards: [
        ["Verify the property", "Before payment, confirm the hotel name, exact address, room availability, stay duration and included services."],
        ["Photos come to you", "We do not publish property photos without owner permission. Actual room photos are shared with you on chat for approval."],
        ["See the final amount", "Taxes and hotel or transport conditions are not assumed. Any applicable extra charges are shared before you confirm."],
      ],
    },
    testimonials: {
      eyebrow: "Guest words",
      title: "Travellers who stayed near the airport",
      note: "Shared by guests on WhatsApp",
      reviews: [
        ["Jimmy Gahlot", "Stay near IGI Airport", "Room was clean and exactly like the photos shared on chat. Pickup was on time and booking with Sudheer ji was easy."],
        ["Priya & Karan", "Couple stay, 6 hours", "Booked the 6-hour couple stay between flights. Transparent price, no hidden charges. Paying advance only after seeing photos felt safe."],
        ["Anil Deshmukh", "Expo at Yashobhoomi", "Stayed a few kilometres from the venue for a trade expo. Airport pickup was on time and the room was exactly as shown on chat."],
        ["Sunita Devi", "Family halt, Kapashera", "Travelling with family toward Gurgaon, we halted near Kapashera. The triple room was clean and budget friendly. Recommended."],
      ],
    },
    faq: {
      eyebrow: "06 · Questions, answered",
      title: "Frequently asked questions",
      items: [
        ["Is my room confirmed instantly?", "No. This website collects an enquiry only. Sudheer Kumar checks availability and provides final confirmation."],
        ["Is ₹1,699 a full-night stay?", "No. The couple stay package at ₹1,699 is for 6 hours only. It is not a full-night stay."],
        ["When do I pay?", "Only after you approve the actual room photos shared with you on chat. A 30% advance payment then confirms the booking."],
        ["Are taxes included in the displayed rates?", "Only what is explicitly stated is shown. Taxes, exact duration and hotel-specific conditions must be confirmed before payment."],
        ["Do you take online payment?", "No online payment is collected through this website. Confirm the final details and terms before paying anyone."],
      ],
    },
    policies: {
      privacy: ["Privacy", "How enquiry details are used", "Your name, contact details and booking preferences are used to respond to this enquiry. They may be shared with the relevant hotel or transport provider only as needed to handle your request. Please avoid sending sensitive personal information in the enquiry."],
      terms: ["Terms & conditions", "Enquiry service terms", "This service helps customers request room and transfer availability. An enquiry is not a confirmed booking. Hotel name, address, availability, duration, inclusions, taxes, final price and property-specific rules must be reviewed before payment or confirmation."],
      cancellation: ["Cancellation & refund", "Advance payment refund rules", "If you have paid the advance and inform us at least 24 hours before your check-in date, the full amount is returned to you. Same-day cancellations are non-refundable. For any other case, Sudheer Kumar will share the applicable terms for your booking before you confirm or pay."],
    },
    areaCommon: {
      eyebrow: "Service area",
      requestRoom: "Request a room",
      whatsapp: "WhatsApp us",
      knowEyebrow: "Good to know",
      knowTitle: "Before you book this area",
      popularEyebrow: "Popular options here",
      ratesFrom: "Rates start from ₹1,699",
      finePrint: "We are a booking assistance service, not a hotel. Exact hotel name, address and availability near this area are confirmed on chat before any payment.",
      bottomCta: "Tell us your dates — we'll check this area for you",
      sendEnquiry: "Send an enquiry",
    },
    footer: {
      about: "Room booking assistance around IGI Airport, Yashobhoomi and Kapashera Border, Delhi. Managed by Sudheer Kumar.",
      call: "Call Now",
      whatsapp: "WhatsApp",
      rights: "Enquiry service only",
      privacy: "Privacy",
      terms: "Terms",
      cancellation: "Cancellation & refund",
      admin: "Owner login",
    },
  },
  hi: {
    nav: { rooms: "कमरे", transfers: "ट्रांसफर", areas: "इलाके", process: "प्रक्रिया", faq: "सवाल-जवाब", cta: "कमरा पूछें" },
    hero: {
      eyebrow: "दिल्ली · कमरा बुकिंग सहायता",
      lines: ["दिल्ली में उतरें।", "कुछ घंटे आराम करें।", "ताज़ा होकर उड़ान भरें।"],
      lede: "IGI एयरपोर्ट के पास भरोसेमंद कमरे — बिना झंझट। सुधीर कुमार खुद उपलब्धता जाँचते हैं और आप एक रुपया देने से पहले कमरे की असली फोटो भेजते हैं।",
      ctaPrimary: "कमरे की पूछताछ करें",
      ctaWhatsapp: "WhatsApp पर बुक करें",
      trust: "हर अनुरोध होटल की उपलब्धता और अंतिम पुष्टि पर निर्भर है।",
      labelTitle: "उदाहरण चित्र",
      labelSub: "असली कमरे की फोटो चैट पर भेजी जाती है।",
    },
    marquee: [
      "IGI एयरपोर्ट · T3 पिकअप", "सिर्फ सत्यापित कमरे", "पहले चैट पर फोटो, फिर भुगतान",
      "पुष्टि के बाद सिर्फ 30% एडवांस", "यशोभूमि कन्वेंशन सेंटर", "कपशेरा बॉर्डर", "24×7 WhatsApp सहायता",
    ],
    disclaimer: {
      lead: "पुष्टि कैसे होती है —",
      text: "जब भी आप पूछताछ करेंगे, हम आपको चैट पर कमरे की असली फोटो भेजेंगे। फोटो पसंद आने के बाद, बुकिंग पक्की करने के लिए 30% एडवांस भुगतान ज़रूरी है।",
    },
    areas: {
      eyebrow: "सेवा क्षेत्र",
      title: "तीन इलाके, एक फोन कॉल",
      intro: "हर पूछताछ खुद संभाली जाती है। शहर का अपना हिस्सा चुनें — हम वहाँ सत्यापित विकल्प जाँचते हैं और असली फोटो चैट पर भेजते हैं।",
      link: "इलाके की जानकारी देखें",
      blurbs: {
        "igi-airport": "ले-ओवर, देर रात या सुबह की उड़ान के लिए एयरपोर्ट के पास कमरे।",
        yashobhoomi: "द्वारका सेक्टर 25 के भारत के सबसे बड़े कन्वेंशन सेंटर के पास ठहरें।",
        "kapashera-border": "दिल्ली और गुड़गांव के बीच हाईवे के किनारे किफायती कमरे।",
      },
    },
    rooms: {
      eyebrow: "01 · कमरों के विकल्प",
      title: "शुरुआत यहाँ से चुनें",
      intro: "दिखाई गई दरें साझा की गई कमरा/पैकेज दरें हैं। होटल का नाम, सही पता, टैक्स, उपलब्धता और शर्तें भुगतान से पहले पुष्टि की जाती हैं। नीचे की फोटो केवल उदाहरण हैं।",
      names: ["कपल स्टे", "डीलक्स रूम · दो लोग", "डीलक्स रूम · नाश्ते सहित", "तीन लोगों का कमरा", "तीन लोग · नाश्ते सहित"],
      notes: ["सिर्फ 6 घंटे — पूरी रात नहीं", "पूरी रात", "पूरी रात · नाश्ता शामिल", "पूरी रात", "पूरी रात · नाश्ता शामिल"],
      tags: ["छोटा ठहराव", "सबसे लोकप्रिय", "नाश्ता", "कमरा", "नाश्ता"],
      enquire: "इस विकल्प के लिए पूछें",
      onEnquiry: "पूछताछ पर",
      aiTag: "AI चित्र · असली फोटो चैट पर",
      noticeLead: ["स्टैंडर्ड चेक-इन:", "स्टैंडर्ड चेक-आउट:"],
      notice: "दोपहर 12:00|सुबह 11:00। सही अवधि और होटल की शर्तें पुष्टि करें।",
    },
    transfers: {
      eyebrow: "02 · एयरपोर्ट आवागमन",
      title: "पिकअप और ड्रॉप, साफ़ कीमत",
      intro: "पिकअप और ड्रॉप अलग-अलग सेवाएँ हैं। पिकअप दरों में पार्किंग शुल्क शामिल है; बुकिंग से पहले अंतिम किराया और उपलब्धता पुष्टि करें।",
      pickup: "पिकअप",
      drop: "ड्रॉप",
      ask: "उपलब्धता पूछें",
      detail: "पिकअप + पार्किंग शुल्क",
      finePrint: "यहाँ कोई टोल, वेटिंग या अन्य शुल्क नहीं जोड़ा गया। लागू होने पर अतिरिक्त शुल्क राइड बुक होने से पहले बता दिया जाता है।",
    },
    process: {
      eyebrow: "03 · आसान और साफ़ प्रक्रिया",
      title: "बिना उलझन के बुकिंग सहायता",
      intro: "यह बुकिंग सहायता सेवा है, तुरंत बुकिंग इंजन नहीं। पुष्टि से पहले सारी जानकारी आपके हाथ में रहती है — और असली कमरा देखे बिना आपसे पैसा नहीं लिया जाता।",
      steps: [
        ["01", "पूछताछ भेजें", "अपनी तारीखें, मेहमान, कमरे की पसंद और ट्रांसफर की ज़रूरत बताएं।"],
        ["02", "उपलब्धता जाँच", "सुधीर कुमार उपलब्ध होटल या ट्रांसपोर्ट विकल्प जाँचते हैं।"],
        ["03", "चैट पर फोटो देखें", "कमरे की असली फोटो WhatsApp पर भेजी जाती है। पहले आप पसंद करते हैं।"],
        ["04", "30% एडवांस से पुष्टि", "पसंद करने के बाद ही 30% एडवांस दें। बाकी डिटेल सीधे साझा की जाती है।"],
      ],
    },
    enquiry: {
      eyebrow: "04 · आपकी पूछताछ",
      title: "हमें बताएं क्या चाहिए",
      intro: "WhatsApp पर अपनी डिटेल भेजें या तैयार ईमेल ड्राफ्ट खोलें। हर पूछताछ सुधीर कुमार के सुरक्षित इनबॉक्स में भी पहुंचती है। यहाँ कोई ऑनलाइन भुगतान नहीं लिया जाता।",
      role: "बुकिंग सहायता · दिल्ली",
      name: "मेहमान का नाम",
      namePh: "आपका पूरा नाम",
      mobile: "मोबाइल / WhatsApp",
      mobilePh: "10 अंकों का नंबर",
      checkIn: "चेक-इन तारीख",
      checkOut: "चेक-आउट या 6 घंटे का स्लॉट",
      checkOutPh: "तारीख या पसंदीदा समय",
      guests: "मेहमानों की संख्या",
      room: "कमरा / पैकेज",
      area: "होटल / इलाका पसंद",
      areaPh: "इलाका चुनें",
      breakfast: "नाश्ता चाहिए",
      transfer: "एयरपोर्ट पिकअप या ड्रॉप?",
      vehicle: "गाड़ी",
      transferType: "ट्रांसफर प्रकार",
      special: "खास अनुरोध",
      specialPh: "फ्लाइट का समय, या कुछ और",
      yes: "हाँ",
      no: "नहीं",
      pickupOpt: "पिकअप",
      dropOpt: "ड्रॉप",
      consent: "मैं इस पूछताछ के लिए संपर्क की सहमति देता/देती हूँ और समझता/समझती हूँ कि बुकिंग उपलब्धता और पुष्टि पर निर्भर है।",
      submit: "WhatsApp पर भेजें",
      sending: "भेजा जा रहा है…",
      email: "ईमेल ड्राफ्ट खोलें",
      success: "इनबॉक्स में सहेजा गया और WhatsApp खुल गया। सुधीर कुमार उपलब्धता बताकर कमरे की फोटो चैट पर भेजेंगे।",
    },
    trust: {
      eyebrow: "05 · पुष्टि से पहले",
      title: "साफ़ जानकारी, बेहतर ठहराव",
      cards: [
        ["प्रॉपर्टी जाँचें", "भुगतान से पहले होटल का नाम, सही पता, कमरे की उपलब्धता, ठहरने की अवधि और सेवाएँ ज़रूर पुष्टि करें।"],
        ["फोटो आपके पास आती है", "हम बिना अनुमति प्रॉपर्टी की फोटो नहीं दिखाते। असली कमरे की फोटो आपकी पसंद के लिए चैट पर भेजी जाती है।"],
        ["अंतिम राशि पहले देखें", "टैक्स और होटल/ट्रांसपोर्ट की शर्तें अनुमानित नहीं होतीं। लागू अतिरिक्त शुल्क पुष्टि से पहले बताए जाते हैं।"],
      ],
    },
    testimonials: {
      eyebrow: "मेहमानों की राय",
      title: "जो मेहमान एयरपोर्ट के पास ठहरे",
      note: "WhatsApp पर मेहमानों द्वारा साझा",
      reviews: [
        ["जिमी गहलोत", "IGI एयरपोर्ट के पास ठहराव", "कमरा साफ था और चैट पर भेजी फोटो जैसा ही था। पिकअप समय पर आया और सुधीर जी के साथ बुकिंग आसान रही।"],
        ["प्रिया और करण", "कपल स्टे, 6 घंटे", "दो फ्लाइटों के बीच 6 घंटे का कपल स्टे लिया। साफ कीमत, कोई छिपा शुल्क नहीं। फोटो देखने के बाद ही एडवांस देना भरोसेमंद लगा।"],
        ["अनिल देशमुख", "यशोभूमि में एक्सपो", "ट्रेड एक्सपो के लिए वेन्यू से कुछ ही किलोमीटर दूर ठहरा। एयरपोर्ट पिकअप समय पर आया और कमरा चैट पर दिखाई गई फोटो जैसा ही था।"],
        ["सुनीता देवी", "पारिवारिक ठहराव, कपशेरा", "गुड़गांव जाते समय परिवार के साथ कपशेरा के पास ठहरे। तीन लोगों वाला कमरा साफ और किफायती था। अनुशंसित।"],
      ],
    },
    faq: {
      eyebrow: "06 · सवाल-जवाब",
      title: "अक्सर पूछे जाने वाले सवाल",
      items: [
        ["क्या मेरा कमरा तुरंत पक्का हो जाता है?", "नहीं। यह वेबसाइट सिर्फ पूछताछ दर्ज करती है। सुधीर कुमार उपलब्धता जाँचकर अंतिम पुष्टि देते हैं।"],
        ["क्या ₹1,699 पूरी रात के लिए है?", "नहीं। ₹1,699 का कपल स्टे पैकेज सिर्फ 6 घंटे के लिए है। यह पूरी रात का ठहराव नहीं है।"],
        ["पैसा कब देना होता है?", "सिर्फ तब, जब आप चैट पर भेजी गई कमरे की असली फोटो पसंद कर लें। इसके बाद 30% एडवांस भुगतान से बुकिंग पक्की होती है।"],
        ["क्या दरों में टैक्स शामिल है?", "केवल वही दिखाया गया है जो स्पष्ट लिखा है। टैक्स, सही अवधि और होटल की शर्तें भुगतान से पहले पुष्टि करें।"],
        ["क्या आप ऑनलाइन पेमेंट लेते हैं?", "इस वेबसाइट पर कोई ऑनलाइन भुगतान नहीं लिया जाता। किसी को भी पैसा देने से पहले अंतिम डिटेल और शर्तें ज़रूर जाँच लें।"],
      ],
    },
    policies: {
      privacy: ["निजता", "पूछताछ की डिटेल कैसे इस्तेमाल होती है", "आपका नाम, संपर्क और बुकिंग पसंद सिर्फ इस पूछताछ के जवाब के लिए इस्तेमाल होते हैं। ज़रूरत होने पर संबंधित होटल या ट्रांसपोर्ट प्रदाता के साथ साझा किए जा सकते हैं। कृपया पूछताछ में संवेदनशील निजी जानकारी न भेजें।"],
      terms: ["नियम व शर्तें", "पूछताछ सेवा की शर्तें", "यह सेवा ग्राहकों को कमरा और ट्रांसफर उपलब्धता पूछने में मदद करती है। पूछताछ पक्की बुकिंग नहीं है। भुगतान या पुष्टि से पहले होटल का नाम, पता, उपलब्धता, अवधि, सेवाएँ, टैक्स, अंतिम कीमत और नियम ज़रूर देख लें।"],
      cancellation: ["रद्दीकरण व रिफंड", "एडवांस भुगतान रिफंड नियम", "यदि आपने एडवांस भुगतान किया है और चेक-इन तारीख से कम से कम 24 घंटे पहले सूचित किया, तो पूरी राशि आपको लौटा दी जाती है। उसी दिन की रद्दीकरण (same-day cancellation) पर राशि रिफंड नहीं होगी। अन्य किसी भी स्थिति में, पुष्टि या भुगतान से पहले सुधीर कुमार आपकी बुकिंग की लागू शर्तें साझा करेंगे।"],
    },
    areaCommon: {
      eyebrow: "सेवा क्षेत्र",
      requestRoom: "कमरे की पूछताछ करें",
      whatsapp: "WhatsApp करें",
      knowEyebrow: "ज़रूरी जानकारी",
      knowTitle: "इस इलाके में बुक करने से पहले",
      popularEyebrow: "यहाँ लोकप्रिय विकल्प",
      ratesFrom: "दरें ₹1,699 से शुरू",
      finePrint: "हम बुकिंग सहायता सेवा हैं, होटल नहीं। इस इलाके के पास सही होटल का नाम, पता और उपलब्धता किसी भी भुगतान से पहले चैट पर पुष्टि की जाती है।",
      bottomCta: "अपनी तारीखें बताएं — हम इस इलाके की जाँच करेंगे",
      sendEnquiry: "पूछताछ भेजें",
    },
    footer: {
      call: "कॉल करें",
      whatsapp: "WhatsApp",
      rights: "केवल पूछताछ सेवा",
      privacy: "निजता",
      terms: "नियम",
      cancellation: "रद्दीकरण व रिफंड",
      admin: "ओनर लॉगिन",
    },
  },
};

const LangContext = createContext({ lang: "en", setLang: () => {}, t: dict.en });

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("site_lang") || "en");
  useEffect(() => {
    localStorage.setItem("site_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  return <LangContext.Provider value={{ lang, setLang, t: dict[lang] }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
